<script lang="ts" setup>
  import { ref, onMounted, onUnmounted, watch, computed } from 'vue';
  import { useToast, useToastContainer } from './composables';
  import Toast from './Toast.vue';

  interface ToastContainerEmits {
    (e: 'close', id: string): void;
    (e: 'life-end', id: string): void;
  }

  const emit = defineEmits<ToastContainerEmits>();

  const isMobile = ref(false);
  const { toasts, zIndex } = useToastContainer();
  const { removeToast } = useToast();

  const computedStyles = computed(() => ({ zIndex: zIndex.value }));

  const handleEmitEvent = (event: Event) => {
    const { emitName, id } = event.detail;
    emit(emitName, id);
  };

  onMounted(() => {
    window.addEventListener('emitEvent', handleEmitEvent);
  });

  onUnmounted(() => {
    window.removeEventListener('emitEvent', handleEmitEvent);
  });

  const onClose = (id: string) => {
    removeToast(id);
  };

  watch(
    toasts,
    (value) => {
      if (value.some((toast) => toast.mobile)) {
        isMobile.value = true;
      } else if (value.length > 0 && value.every((toast) => !toast.mobile)) {
        isMobile.value = false;
      }
    },
    { deep: true },
  );
</script>

<template>
  <div :class="['tl-toast-container', { 'tl-toast-container_mobile': isMobile }]" :style="computedStyles">
    <TransitionGroup name="fade" tag="div" class="tl-toast-container__list">
      <div v-for="item in toasts" :key="item.id" class="tl-toast-container__list-item" tabindex="0">
        <Toast v-bind="item" @close="onClose(item.id)">
          <template #content>
            <slot name="content" />
          </template>

          <template #footer>
            <slot name="footer" />
          </template>
        </Toast>
      </div>
    </TransitionGroup>
  </div>
</template>

<style lang="scss" scoped>
  .tl-toast-container {
    $root: &;

    align-items: end;
    display: flex;
    inset: 0 50px 50px 0;
    pointer-events: none;
    position: fixed;

    &_mobile {
      inset: 0 0 var(--spacing-xxxl) 0;

      #{$root}__list {
        padding: 0 var(--spacing-m);
      }
    }

    &__list {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-m);
      list-style: none;
      margin: unset;
      padding: unset;
      pointer-events: none;
      width: 100%;

      &-item {
        display: flex;
        justify-content: end;
        pointer-events: all;
      }
    }
  }
</style>
